/* eslint-disable @typescript-eslint/no-explicit-any */
import { useMemo } from 'react'
import { Text } from '@radix-ui/themes'
import { useQuery } from '@tanstack/react-query'
import { moduleService } from '@/services/modulesService'
import { QuestionDetail } from '@/types/question'
import BasicInfoForm from '@/components/ui/CreateSection/basic-info-form'
import SectionTable from '@/components/ui/CreateSection/section-table'
import TitleBar from '@/components/ui/CreateSection/title-bar'
import Stack from '@/components/ui/stack'
import TextEditor from '@/components/ui/text-editor'

interface Props {
  moduleId: string
  onSaveSelected: (payload: {
    section: string
    questionIds: string[]
    items: QuestionDetail[]
    caseStudyGroups: Array<{
      caseStudyId: number
      items: any[]
      totalTime: number
      totalMarks: number
      selectedCount: number
    }>
    totals?: { totalQuestions: number; totalMarks: number; totalTime: number }
    isUserInteraction?: boolean
  }) => void
  name: string
  onNameChange: (value: string) => void
  generalInfo?: string
  onGeneralInfoChange?: (value: string) => void
  readOnly?: boolean
  classDetail?: any
  status?: string
  totalQuestions?: number
  totalMarks?: number
  durationMinutes?: number
  onDurationChange?: (value: number) => void
  readingTimerEnabled?: boolean
  readingDurationSeconds?: number
  onReadingTimerEnabledChange?: (enabled: boolean) => void
  onReadingDurationSecondsChange?: (seconds: number) => void
  initialSelectedQuestions?: Record<string, any[]>
  initialSelectedCaseStudies?: Record<string, any[]>
  initialTitle?: string
}

export default function CreateView({
  moduleId,
  onSaveSelected,
  name,
  onNameChange,
  generalInfo,
  onGeneralInfoChange,
  readOnly,
  status,
  totalQuestions,
  totalMarks,
  durationMinutes,
  onDurationChange,
  readingTimerEnabled,
  readingDurationSeconds,
  onReadingTimerEnabledChange,
  onReadingDurationSecondsChange,
  initialSelectedQuestions,
  initialSelectedCaseStudies,
  initialTitle,
}: Props) {
  const { data: sections } = useQuery({
    queryKey: ['sections', moduleId],
    queryFn: () => moduleService.getSectionsByModuleId(Number(moduleId)),
  })

  const { data: moduleConfig } = useQuery({
    queryKey: ['moduleConfig'],
    queryFn: () => moduleService.getModuleConfig(),
    staleTime: 5 * 60 * 1000, // 5 minutes
    enabled: !!moduleId,
  })

  // Determine default sections based on module config. Always include defaults even if API returns fewer.
  const sectionsToRender = useMemo(() => {
    const isModuleNeedSheet = moduleConfig?.need_sheet_module_id?.includes(
      Number(moduleId)
    )
    const defaultSections = isModuleNeedSheet ? ['A', 'B', 'C'] : ['A', 'B']

    const apiSections = Array.isArray(sections?.sections)
      ? sections.sections
      : []
    const merged = Array.from(
      new Set([...(apiSections as string[]), ...defaultSections])
    )

    const order = ['A', 'B', 'C']
    return merged
      .filter((s) => order.includes(s))
      .sort((a, b) => order.indexOf(a) - order.indexOf(b))
  }, [moduleConfig?.need_sheet_module_id, moduleId, sections?.sections])

  return (
    <Stack gap={'24px'} className='bg-[#F6F7F9] px-[40px]'>
      <TitleBar
        title={initialTitle || 'New Practice Test'}
        status={status}
        totalQuestions={totalQuestions}
        totalMarks={totalMarks}
        durationMinutes={durationMinutes}
        onDurationChange={onDurationChange}
        readingTimerEnabled={readingTimerEnabled}
        readingDurationSeconds={readingDurationSeconds}
        onReadingTimerEnabledChange={onReadingTimerEnabledChange}
        onReadingDurationSecondsChange={onReadingDurationSecondsChange}
      />
      <Stack gap={'16px'}>
        <BasicInfoForm
          name={name}
          moduleId={Number(moduleId)}
          onNameChange={onNameChange}
          readOnly={readOnly}
        />
        <Text className='text-[24px] font-[400] text-[#002652]'>
          General Information
        </Text>
        <TextEditor
          value={generalInfo || ''}
          onChange={(v) => onGeneralInfoChange?.(v)}
          disableImage={true}
          className='box-shadow: 0px 1px 2px 0px #1018280D; min-h-[100px] rounded-md border border-[#D0D5DD] p-2'
        />
        {sectionsToRender
          ?.sort((a, b) => {
            const order = ['A', 'B', 'C']
            return order.indexOf(a) - order.indexOf(b)
          })
          ?.map((section) => (
            <SectionTable
              key={section}
              title={section}
              moduleId={moduleId}
              onSaveSelected={onSaveSelected}
              initialSelectedQuestions={initialSelectedQuestions?.[section]}
              initialSelectedCaseStudies={initialSelectedCaseStudies?.[section]}
            />
          ))}
      </Stack>
    </Stack>
  )
}
